import React, { useEffect, useState, useRef } from "react";
import { HelixIcon } from "@helix/helix-icon";
import { circle_checkmark as CircleCheckMarkIcon } from "@helix/helix-icon/solid";
import { fetchAccountOptions } from "../../api/session";
import { getCSRFParam, getCSRFToken } from "../../utils/page";
import AccountPicker from "./AccountPicker";
import { buildForgotPasswordUrl } from "./urls";
import { CloudCmaLogo, ErrorIcon } from "./icons";
import {
  Content,
  Flash,
  Form,
  FormGroup,
  Logo,
  Reset,
  SubmitButton,
  SubmitContainer,
  Text,
  Title
} from "./styled/login";
import Input from "../common/Input";
import Label from "../common/Label";

const CSRF_PARAM = getCSRFParam();
const CSRF_TOKEN = getCSRFToken();

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STEP = {
  EMAIL: "email",
  SELECT_ACCOUNT: "selectAccount",
  READY: "ready"
};

const LW_SSO_ACCOUNT_TYPE = "platform_client_brokerage";

function ForgotPassword({
  action = "/password",
  forgotPasswordUrl = "/password/new",
  loginUrl = "/login",
  lwSsoUrl = "#",
  flash = {},
  errors = [],
  email: initialEmail = "",
  accountId: initialAccountId = null
}) {
  const [step, setStep] = useState(
    initialEmail && initialAccountId ? STEP.READY : STEP.EMAIL
  );
  const [email, setEmail] = useState(initialEmail || "");
  const [accounts, setAccounts] = useState([]);
  const [accountsEmail, setAccountsEmail] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState(initialAccountId || null);
  const [loading, setLoading] = useState(false);
  const [sendingInstructions, setSendingInstructions] = useState(false);
  const [message, setMessage] = useState(() => {
    const [error] = errors || [];
    if (error) return { text: error, isError: true };

    const [entry] = Object.entries(flash || {});
    if (!entry) return null;
    const [key, text] = entry;
    return { text, isError: key === "alert" || key === "error" };
  });

  const requestIdRef = useRef(0);
  const formRef = useRef(null);
  const emailInputRef = useRef(null);

  useEffect(() => {
    if (!initialEmail || !initialAccountId) return;

    const requestId = ++requestIdRef.current;
    setLoading(true);

    fetchAccountOptions(initialEmail)
      .then((results) => {
        if (requestId !== requestIdRef.current) return;
        setAccounts(results);
        setAccountsEmail(initialEmail);
      })
      .catch(() => {})
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      });
  }, [initialEmail, initialAccountId]);

  useEffect(() => {
    if (!sendingInstructions) return;

    formRef.current?.submit();
  }, [sendingInstructions, selectedAccountId]);

  function showError(text) {
    setMessage({ text, isError: true });
  }

  function clearMessage() {
    setMessage(null);
  }

  function syncUrl(accountId) {
    const url = accountId
      ? buildForgotPasswordUrl(forgotPasswordUrl, email, accountId)
      : forgotPasswordUrl;

    if (window.location.pathname + window.location.search === url) return;

    window.history.replaceState({}, "", url);
  }

  function redirectToAccount(accountId) {
    requestIdRef.current += 1;
    setLoading(true);
    window.location.href = buildForgotPasswordUrl(
      forgotPasswordUrl,
      email,
      accountId
    );
  }

  function resolveAccounts(results) {
    if (!results.length) {
      showError("We couldn't find a Cloud CMA account for that email.");
      return;
    }

    if (results.length === 1) {
      const [account] = results;

      if (account.accountType === LW_SSO_ACCOUNT_TYPE) {
        window.location.href = lwSsoUrl;
        return;
      }

      setSelectedAccountId(account.accountId);
      setStep(STEP.READY);
      syncUrl(account.accountId);
      setSendingInstructions(true);
      return;
    }

    setStep(STEP.SELECT_ACCOUNT);
  }

  async function handleContinue() {
    if (!EMAIL_RE.test(email)) {
      showError("Please enter a valid email address.");
      return;
    }

    if (accountsEmail === email) {
      clearMessage();
      resolveAccounts(accounts);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    clearMessage();

    try {
      const results = await fetchAccountOptions(email);

      if (requestId !== requestIdRef.current) {
        return;
      }

      setAccounts(results);
      setAccountsEmail(email);
      resolveAccounts(results);
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      showError(err.message || "An error occurred while looking up your account.");
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }

  function handleAccountSelected(account) {
    if (!account) return;

    redirectToAccount(account.accountId);
  }

  function handleSendInstructions() {
    clearMessage();
    setSendingInstructions(true);
  }

  function handleFormSubmit(event) {
    event.preventDefault();

    if (step === STEP.READY) {
      handleSendInstructions();
    } else {
      handleContinue();
    }
  }

  function resetToEmail(event) {
    if (event) event.preventDefault();

    requestIdRef.current += 1;
    setLoading(false);
    setStep(STEP.EMAIL);
    setSelectedAccountId(null);
    clearMessage();
    syncUrl(null);
    setTimeout(() => emailInputRef.current?.focus(), 0);
  }

  if (step === STEP.SELECT_ACCOUNT) {
    return (
      <>
        <Content expandContainer>
          <Logo>
            <CloudCmaLogo />
          </Logo>
          <AccountPicker
            lwSsoUrl={lwSsoUrl}
            email={email}
            accounts={accounts}
            onContinue={handleAccountSelected}
          />
        </Content>
        <Reset>
          <a href={forgotPasswordUrl} onClick={resetToEmail}>
            Use a different email
          </a>
        </Reset>
      </>
    );
  }

  const isReady = step === STEP.READY;

  const submitLabel = sendingInstructions
    ? "Sending Instructions"
    : isReady
      ? "Email Instructions"
      : "Continue";
      
  const lockedToAccount = isReady && accountsEmail === email && accounts.length > 1;
  const selectedAccountName = lockedToAccount
    ? accounts.find(
        (account) => String(account.accountId) === String(selectedAccountId)
      )?.name
    : null;

  return (
    <Content>
      <Logo>
        <CloudCmaLogo />
      </Logo>
      <Form
        ref={formRef}
        action={action}
        method="post"
        onSubmit={handleFormSubmit}>
        <Title>Need your password reset?</Title>
        <Text>
          Please enter your email and we’ll send you instructions on how to
          reset your password
        </Text>
        <input type="hidden" name={CSRF_PARAM} value={CSRF_TOKEN} />
        <input
          type="hidden"
          name="user[account_id]"
          value={selectedAccountId ?? ""}
        />
        <FormGroup>
          <Label htmlFor="user_email">Email</Label>
          <Input
            ref={emailInputRef}
            id="user_email"
            type="email"
            name="user[email]"
            placeholder="john@acmerealty.com"
            autoComplete="email"
            value={email}
            readOnly={lockedToAccount}
            onChange={(event) => {
              setEmail(event.target.value);
              setStep(STEP.EMAIL);
              setSelectedAccountId(null);
              clearMessage();
              syncUrl(null);
            }}
          />
        </FormGroup>
        {selectedAccountName && (
          <FormGroup>
            <Flash $center role="status">
              <HelixIcon icon={CircleCheckMarkIcon} />
              Password reset for{" "}{selectedAccountName}
            </Flash>
          </FormGroup>
        )}
        <SubmitContainer>
          <SubmitButton
            type="submit"
            disabled={loading || sendingInstructions || !email.trim()}>
            {submitLabel}
          </SubmitButton>
        </SubmitContainer>
        {message && (
          <FormGroup $top>
            <Flash $error={message.isError} role="alert">
              {message.isError ? (
                <ErrorIcon />
              ) : (
                <HelixIcon icon={CircleCheckMarkIcon} />
              )}
              {message.text}
            </Flash>
          </FormGroup>
        )}
      </Form>
      {lockedToAccount && (
        <Reset>
          <a href={forgotPasswordUrl} onClick={resetToEmail}>
            Use a different email
          </a>
        </Reset>
      )}
      <Reset>
        <a href={loginUrl}>Return to Login</a>
      </Reset>
    </Content>
  );
}

export default ForgotPassword;
